import React, { useState } from "react";
import { Anchor, Box, Table, Text } from "@mantine/core";
import InfoTooltip, { MetricInfo } from "../../../common/InfoTooltip";
import ReportBreakdownTable from "../../ReportBreakdownTable";
import { isServiceCommissionOverridesEnabled } from "../../../../utils/serviceCommissionOverridesFlag";
import {
  ReportLedger,
  ReportLedgerColumn,
  ReportLedgerRow,
} from "../../../../types/reportsV2";
import { formatMetricValue } from "../../../../utils/metricFormat";
import styles from "./LedgerTable.module.css";

export interface LedgerTableProps {
  ledger: ReportLedger;
  /** Tighter padding for the phone layout. */
  compact?: boolean;
  /** No info icons (print). */
  plain?: boolean;
}

const valueCells = (columns: ReportLedgerColumn[], values: number[]) =>
  columns.map((column, i) => (
    <Table.Td key={column.key} className={styles.value}>
      {formatMetricValue(values[i], column.format)}
    </Table.Td>
  ));

function RowLabel({
  row,
  plain,
  trailing,
}: {
  row: ReportLedgerRow;
  plain: boolean;
  /** Rendered after the info icon (the drill-down's "(expand)" toggle). */
  trailing?: React.ReactNode;
}) {
  const help = !plain && (row.description || row.formula);
  return (
    <Box className={styles.labelCell}>
      <Text component="span" inherit>
        {row.label}
      </Text>
      {help && (
        <InfoTooltip
          ariaLabel={`About ${row.label}`}
          label={
            <MetricInfo description={row.description} formula={row.formula} />
          }
        />
      )}
      {trailing}
    </Box>
  );
}

/** A row's drill-down, when it has rows and the feature is on (not in print). */
const drilldownOf = (row: ReportLedgerRow, plain: boolean) =>
  !plain &&
  isServiceCommissionOverridesEnabled() &&
  row.drilldown &&
  row.drilldown.rows.length > 0
    ? row.drilldown
    : null;

interface LedgerRowViewProps {
  row: ReportLedgerRow;
  columns: ReportLedgerColumn[];
  plain: boolean;
}

/** One ledger row, plus its expandable drill-down table when it has one. */
function LedgerRowView({ row, columns, plain }: LedgerRowViewProps) {
  const [open, setOpen] = useState(false);
  const drilldown = drilldownOf(row, plain);
  return (
    <>
      <Table.Tr data-informational={row.informational || undefined}>
        <Table.Td>
          <RowLabel
            row={row}
            plain={plain}
            trailing={
              drilldown && (
                <Anchor
                  component="button"
                  type="button"
                  className={styles.expandToggle}
                  aria-label={`${open ? "Collapse" : "Expand"} ${drilldown.label}`}
                  aria-expanded={open}
                  onClick={() => setOpen((v) => !v)}
                >
                  {open ? "(collapse)" : "(expand)"}
                </Anchor>
              )
            }
          />
          {row.informational && (
            <Text className={styles.note}>Not included in total</Text>
          )}
        </Table.Td>
        {valueCells(columns, row.values)}
      </Table.Tr>
      {drilldown && open && (
        <Table.Tr>
          {/* maxWidth 0: the nested table scrolls inside this cell instead of
              widening the ledger and pushing its value columns off-screen. */}
          <Table.Td
            colSpan={columns.length + 1}
            className={styles.drilldownCell}
            style={{ maxWidth: 0 }}
          >
            <ReportBreakdownTable
              label={drilldown.label}
              columns={drilldown.columns}
              rows={drilldown.rows}
            />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

/**
 * One summary ledger: the label column ("Sales category"), its value columns
 * (e.g. Payments / Refunds / Total) and a bold total row. Informational rows
 * (e.g. package redemptions) are muted and marked "not in total"; every row
 * label and its help text come verbatim from the reports service.
 */
export default function LedgerTable({
  ledger,
  compact = false,
  plain = false,
}: LedgerTableProps) {
  const { columns } = ledger;
  return (
    <Box
      className={styles.card}
      data-compact={compact || undefined}
      data-testid={`ledger-${ledger.key}`}
    >
      <Table className={styles.table} aria-label={ledger.label}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className={styles.labelHeader}>{ledger.label}</Table.Th>
            {columns.map((column) => (
              <Table.Th key={column.key} className={styles.valueHeader}>
                {column.label}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {ledger.rows.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={columns.length + 1} className={styles.empty}>
                Nothing in this period.
              </Table.Td>
            </Table.Tr>
          )}
          {ledger.rows.map((row) => (
            <LedgerRowView
              key={row.key}
              row={row}
              columns={columns}
              plain={plain}
            />
          ))}
        </Table.Tbody>
        {ledger.total && (
          <Table.Tfoot>
            <Table.Tr className={styles.totalRow}>
              <Table.Td>{ledger.total.label}</Table.Td>
              {valueCells(columns, ledger.total.values)}
            </Table.Tr>
          </Table.Tfoot>
        )}
      </Table>
    </Box>
  );
}
